/**
 * Web ScrollView Component
 *
 */

import { useContext, useRef, useState } from 'react'
import type { DetailedHTMLProps, HTMLAttributes, Ref } from 'react'
import clsx from 'clsx'
import {
  extendPropsWithContext,
  validateDOMAttributes,
} from '../../shared/component-helper'
import Context from '../../shared/Context'
import { useSpacing } from '../../components/space/SpacingUtils'
import type { SpacingProps } from '../../shared/types'

import { useIsomorphicLayoutEffect as useLayoutEffect } from '../../shared/helpers/useIsomorphicLayoutEffect'
import useCombinedRef from '../../shared/helpers/useCombinedRef'
import withComponentMarkers from '../../shared/helpers/withComponentMarkers'

export type ScrollViewProps = {
  /**
   * To make the content accessible to keyboard navigation. Use `true` or `auto`. Auto will detect if a scrollbar is visible and make the ScrollView accessible for keyboard navigation.
   * Default: `false`
   */
  interactive?: boolean | 'auto'

  /**
   * Reserves space for the scrollbar gutter, preventing layout shifts when content overflows. Maps to the CSS `scrollbar-gutter` property.
   * Default: `undefined`
   */
  scrollbarGutter?: 'stable'
}

export type ScrollViewAllProps = ScrollViewProps &
  SpacingProps &
  Partial<Omit<HTMLAttributes<HTMLDivElement>, 'title'>> & {
    ref?: Ref<unknown>
  }

function ScrollView(localProps: ScrollViewAllProps) {
  const context = useContext(Context)

  // use only the props from context, who are available here anyway
  const props = extendPropsWithContext(localProps, {}, context.ScrollView)

  const {
    interactive,
    scrollbarGutter,
    children,
    className = null,
    ref: refProp,
    ...attributes
  } = props

  const mainParams: DetailedHTMLProps<
    HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > = useSpacing(props, {
    ...(attributes as HTMLAttributes<unknown>),
    className: clsx(
      'dnb-scroll-view',
      scrollbarGutter === 'stable' &&
        'dnb-scroll-view--scrollbar-gutter-stable',
      className
    ),
  })

  const localRef = useRef<HTMLDivElement>(undefined)
  const combinedRef = useCombinedRef(refProp, localRef)
  mainParams.ref = combinedRef

  mainParams.tabIndex = useInteractive({
    interactive,
    children,
    ref: localRef,
  })

  validateDOMAttributes(props, mainParams)

  return <div {...mainParams}>{children}</div>
}

function useInteractive({ interactive, children, ref }) {
  const [isInteractive, setAsInteractive] = useState(Boolean(interactive))

  useLayoutEffect(() => {
    if (interactive === 'auto') {
      setAsInteractive(hasScrollbar())
    }
  }, [interactive, children]) // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (interactive === 'auto' && typeof ResizeObserver !== 'undefined') {
      // eslint-disable-next-line compat/compat
      const observer = new ResizeObserver(() => {
        setAsInteractive(hasScrollbar())
      })
      observer.observe(ref.current)
      return () => observer?.disconnect()
    }

    return undefined
  }, [interactive, ref]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isInteractive) {
    return 0 // Ensure that scrollable region has keyboard access
  }

  return undefined

  function hasScrollbar() {
    if (!ref.current) {
      return true // fallback and assume, there is a scrollbar
    }

    /**
     * Safari Desktop adds one pixel "on zoom" level 1
     * therefore we just remove it here
     */
    return (
      ref.current.scrollWidth - 1 > ref.current.offsetWidth ||
      ref.current.scrollHeight - 1 > ref.current.offsetHeight
    )
  }
}

withComponentMarkers(ScrollView, {
  _supportsSpacingProps: true,
})

export default ScrollView
